import { MouseEvent, useCallback, useRef, useState } from 'react'

import EncounterAbilityLabel from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/EncounterAbilityLabel'
import TimelineAbilitySection from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TimelineAbilitySection'
import TimelineContainer from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TimelineContainer'
import { useTeamPlanTimeline } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TeamPlanTimelineContext'
import {
  applyPhaseBoundaryChange,
  buildPhaseDragBaseline,
  type PhaseDragBaseline,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/utils/phaseDurationEdit'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const AssignmentDefaultsEncounterTimeline = () => {
  const { computed, timeline, updateTimeline } = useTeamPlanTimeline()
  const {
    encounter,
    hasEncounterTimeline,
    maxTime,
    phaseDividers,
    phaseBoundaryHandles,
    encounterAbilityRows,
    phaseOrder,
    effectivePhaseDurations,
    phaseStarts,
    phaseEnds,
  } = computed
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [dragFrameMax, setDragFrameMax] = useState<number | null>(null)
  const phaseDragBaselineRef = useRef<PhaseDragBaseline | null>(null)
  const boundaryDragActiveRef = useRef(false)
  const displayMaxTime =
    dragFrameMax !== null && dragFrameMax > maxTime ? dragFrameMax : maxTime

  const capturePhaseDragBaseline = useCallback(() => {
    phaseDragBaselineRef.current = buildPhaseDragBaseline({
      timeline,
      phaseOrder,
      phaseStarts,
      phaseEnds,
      durations: effectivePhaseDurations,
      handles: phaseBoundaryHandles,
    })
  }, [
    effectivePhaseDurations,
    phaseBoundaryHandles,
    phaseEnds,
    phaseOrder,
    phaseStarts,
    timeline,
  ])

  const applyPhaseBoundary = useCallback(
    (boundaryIndex: number, nextAt: number) => {
      if (!phaseDragBaselineRef.current) capturePhaseDragBaseline()
      const baseline = phaseDragBaselineRef.current
      if (!baseline) return
      const transformed = applyPhaseBoundaryChange(
        baseline,
        boundaryIndex,
        nextAt,
      )
      if (!transformed) return
      updateTimeline((next) => {
        Object.keys(next).forEach((key) => {
          delete (next as Record<string, unknown>)[key]
        })
        Object.assign(next, transformed)
      })
    },
    [capturePhaseDragBaseline, updateTimeline],
  )

  const handlePhaseBoundaryDragStart = useCallback(
    (_boundaryIndex: number, at: number) => {
      const baselineSum = effectivePhaseDurations.reduce(
        (sum, duration) => sum + duration,
        0,
      )
      setDragFrameMax(baselineSum)
      capturePhaseDragBaseline()
      boundaryDragActiveRef.current = true
      setHoverTime(at)
    },
    [capturePhaseDragBaseline, effectivePhaseDurations],
  )

  const handlePhaseBoundaryDrag = useCallback(
    (boundaryIndex: number, nextAt: number) => {
      applyPhaseBoundary(boundaryIndex, nextAt)
      setHoverTime(nextAt)
    },
    [applyPhaseBoundary],
  )

  const handlePhaseBoundaryDragEnd = useCallback(
    (boundaryIndex: number, nextAt: number) => {
      applyPhaseBoundary(boundaryIndex, nextAt)
      phaseDragBaselineRef.current = null
      setDragFrameMax(null)
      boundaryDragActiveRef.current = false
      setHoverTime(nextAt)
    },
    [applyPhaseBoundary],
  )

  const handleResetPhaseDuration = useCallback(
    (phaseIndex: number) => {
      const handle = phaseBoundaryHandles.find(
        (entry) => entry.index === phaseIndex,
      )
      if (!handle) return
      capturePhaseDragBaseline()
      applyPhaseBoundary(phaseIndex, handle.max)
      phaseDragBaselineRef.current = null
    },
    [applyPhaseBoundary, capturePhaseDragBaseline, phaseBoundaryHandles],
  )

  const handleTimelineHoverMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (boundaryDragActiveRef.current) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0) {
        setHoverTime(null)
        return
      }
      const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1)
      setHoverTime(Math.floor(clamp(ratio * displayMaxTime, 0, displayMaxTime)))
    },
    [displayMaxTime],
  )

  const handleTimelineHoverLeave = useCallback(() => {
    if (boundaryDragActiveRef.current) return
    setHoverTime(null)
  }, [])

  return (
    <TimelineAbilitySection
      title={
        <div className='flex items-center gap-1.5 min-w-0'>
          {encounter?.thumbnailUrl ? (
            <img
              className='w-4 h-4 rounded-md shrink-0'
              src={encounter.thumbnailUrl}
            />
          ) : (
            <div className='w-4 h-4 rounded-md border bg-muted' />
          )}
          <span className='truncate'>{encounter?.name}</span>
        </div>
      }
      labels={encounterAbilityRows.map((row) => (
        <EncounterAbilityLabel
          key={`${row.spellId}-${row.name}`}
          spellId={row.spellId}
          name={row.name}
          color={row.color}
          categories={row.categories}
        />
      ))}
      timeline={
        <TimelineContainer
          maxTime={displayMaxTime}
          phaseDividers={hasEncounterTimeline ? phaseDividers : []}
          phaseBoundaryHandles={
            hasEncounterTimeline ? phaseBoundaryHandles : []
          }
          rows={hasEncounterTimeline ? encounterAbilityRows : []}
          hoverTime={hoverTime}
          onPhaseBoundaryDragStart={
            hasEncounterTimeline ? handlePhaseBoundaryDragStart : undefined
          }
          onPhaseBoundaryDrag={
            hasEncounterTimeline ? handlePhaseBoundaryDrag : undefined
          }
          onPhaseBoundaryDragEnd={
            hasEncounterTimeline ? handlePhaseBoundaryDragEnd : undefined
          }
          onResetPhaseDuration={
            hasEncounterTimeline ? handleResetPhaseDuration : undefined
          }
          emptyMessage={
            hasEncounterTimeline
              ? undefined
              : 'Timeline information will be available for this encounter soon'
          }
          className='text-card-nested'
        />
      }
      onTimelineMouseMove={handleTimelineHoverMove}
      onTimelineMouseLeave={handleTimelineHoverLeave}
    />
  )
}

export default AssignmentDefaultsEncounterTimeline
